/* JMT core: utilities, native-dialog drawers, header behavior, mega menu, drill-down nav,
   announcement rotator, footer accordions and the wishlist store. No dependencies. */
(() => {
  'use strict';

  const jmt = (window.jmt = window.jmt || {});
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };
  const store = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (error) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* storage unavailable (private mode): fail silently */
      }
    },
  };
  jmt.utils = { $, $$, debounce, store, reduceMotion };

  /* ---------- Drawers (native <dialog>: focus trap, Esc and inert background come from the browser) ---------- */
  const openers = new WeakMap();

  jmt.drawer = {
    open(id, opener) {
      const dialog = document.getElementById(id);
      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
      if (opener) openers.set(dialog, opener);
      dialog.showModal();
      dialog.getBoundingClientRect(); // flush styles so the entry transition runs
      requestAnimationFrame(() => dialog.classList.add('is-visible'));
      dialog.dispatchEvent(new CustomEvent('jmt:drawer:open', { bubbles: true, detail: { id } }));
    },
    close(dialog) {
      if (!dialog || !dialog.open || dialog.dataset.closing) return;
      dialog.dataset.closing = 'true';
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        delete dialog.dataset.closing;
        dialog.close();
        dialog.dispatchEvent(new CustomEvent('jmt:drawer:close', { bubbles: true }));
        const opener = openers.get(dialog);
        if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      };
      dialog.classList.remove('is-visible');
      if (reduceMotion()) return finish();
      const panel = $('.jmt-drawer__panel', dialog);
      if (panel) panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 340);
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-jmt-open]');
    if (opener) {
      event.preventDefault();
      jmt.drawer.open(opener.dataset.jmtOpen, opener);
      return;
    }
    const closer = event.target.closest('[data-jmt-close]');
    if (closer) {
      jmt.drawer.close(closer.closest('dialog'));
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.matches('[data-jmt-drawer]')) {
      jmt.drawer.close(event.target); // click on the backdrop
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement && event.target.matches('[data-jmt-drawer]')) {
        event.preventDefault(); // Esc: close with the exit transition
        jmt.drawer.close(event.target);
      }
    },
    true
  );

  /* ---------- Mobile drill-down navigation ---------- */
  const navDrawer = $('#jmt-nav-drawer');
  if (navDrawer) {
    const title = $('[data-jmt-drawer-title]', navDrawer);
    const back = $('[data-jmt-back]', navDrawer);
    let lastRow = null;

    const showPane = (key, label, moveFocus = true) => {
      $$('[data-jmt-pane]', navDrawer).forEach((pane) => {
        const active = pane.dataset.jmtPane === key;
        pane.classList.toggle('is-active', active);
        pane.toggleAttribute('inert', !active);
        if (active) pane.scrollTop = 0;
      });
      const isRoot = key === 'root';
      back.hidden = isRoot;
      title.textContent = isRoot ? title.dataset.rootTitle : label;
      if (!moveFocus) return;
      requestAnimationFrame(() => {
        const pane = $(`[data-jmt-pane="${key}"]`, navDrawer);
        const target = isRoot ? lastRow : $('a, button', pane);
        (target || back).focus({ preventScroll: true });
      });
    };

    navDrawer.addEventListener('click', (event) => {
      const row = event.target.closest('[data-jmt-pane-open]');
      if (row) {
        lastRow = row;
        showPane(row.dataset.jmtPaneOpen, row.dataset.title);
      } else if (event.target.closest('[data-jmt-back]')) {
        showPane('root', '');
      }
    });
    navDrawer.addEventListener('jmt:drawer:close', () => showPane('root', '', false));
  }

  /* ---------- Header: scroll state, hide on scroll (mobile) and mega menu (desktop) ---------- */
  const header = $('[data-jmt-header]');
  if (header) {
    const desktop = window.matchMedia('(min-width: 1280px)');
    const hideOnScroll = header.dataset.hideOnScroll === 'true';
    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      const y = Math.max(window.scrollY, 0);
      header.classList.toggle('is-scrolled', y > 0);
      if (hideOnScroll && !desktop.matches) {
        const delta = y - lastY;
        if (y < 80 || delta < -6) header.removeAttribute('data-hidden');
        else if (delta > 6) header.setAttribute('data-hidden', '');
      } else {
        header.removeAttribute('data-hidden');
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(onScroll);
        }
      },
      { passive: true }
    );
    header.addEventListener('focusin', () => header.removeAttribute('data-hidden'));
    onScroll();

    const items = $$('[data-jmt-nav-item]', header).filter((item) => $('[data-jmt-mega-trigger]', item));
    let openItem = null;
    let openTimer;
    let closeTimer;
    const triggerOf = (item) => $('[data-jmt-mega-trigger]', item);
    const panelOf = (item) => document.getElementById(triggerOf(item).getAttribute('aria-controls'));

    const closeMega = () => {
      if (!openItem) return;
      const panel = panelOf(openItem);
      if (panel) panel.hidden = true;
      triggerOf(openItem).setAttribute('aria-expanded', 'false');
      openItem = null;
    };
    const openMega = (item) => {
      clearTimeout(closeTimer);
      if (openItem === item) return;
      closeMega();
      const panel = panelOf(item);
      if (!panel) return;
      panel.hidden = false;
      triggerOf(item).setAttribute('aria-expanded', 'true');
      openItem = item;
    };

    items.forEach((item) => {
      const trigger = triggerOf(item);
      item.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => openMega(item), 120);
      });
      item.addEventListener('mouseleave', () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(closeMega, 200);
      });
      trigger.addEventListener('click', () => (openItem === item ? closeMega() : openMega(item)));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMega(item);
          const first = $('a', panelOf(item));
          if (first) first.focus();
        }
      });
    });
    $$('[data-jmt-mega]', header).forEach((panel) => {
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(closeMega, 200);
      });
    });
    header.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openItem) {
        const trigger = triggerOf(openItem);
        closeMega();
        trigger.focus();
      }
    });
    header.addEventListener('focusout', (event) => {
      if (!openItem || !event.relatedTarget) return;
      if (!openItem.contains(event.relatedTarget) && !panelOf(openItem).contains(event.relatedTarget)) closeMega();
    });
    document.addEventListener('click', (event) => {
      if (openItem && !header.contains(event.target)) closeMega();
    });
    desktop.addEventListener('change', closeMega);
  }

  /* ---------- Announcement rotator ---------- */
  $$('[data-jmt-announcement]').forEach((bar) => {
    const messages = $$('.jmt-announcement__msg', bar);
    if (messages.length < 2 || bar.dataset.autoplay !== 'true' || reduceMotion()) return;
    let index = 0;
    let paused = false;
    setInterval(() => {
      if (paused || document.hidden) return;
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, (Number(bar.dataset.speed) || 5) * 1000);
    ['mouseenter', 'focusin'].forEach((name) => bar.addEventListener(name, () => (paused = true)));
    ['mouseleave', 'focusout'].forEach((name) => bar.addEventListener(name, () => (paused = false)));
  });

  /* ---------- Footer accordions: collapsed on mobile, static columns on desktop ---------- */
  const footerCols = $$('[data-jmt-footer-col]');
  if (footerCols.length) {
    const desktopFooter = window.matchMedia('(min-width: 1024px)');
    const sync = () => footerCols.forEach((col) => (col.open = desktopFooter.matches));
    sync();
    desktopFooter.addEventListener('change', sync);
    footerCols.forEach((col) => {
      $('summary', col).addEventListener('click', (event) => {
        if (desktopFooter.matches) event.preventDefault();
      });
    });
  }

  /* ---------- Wishlist (stored in the browser; no account needed) ---------- */
  const WISHLIST_KEY = 'jmt:wishlist';
  const wishlist = (jmt.wishlist = {
    ids: new Set(store.get(WISHLIST_KEY, []).map(String)),
    has(id) {
      return this.ids.has(String(id));
    },
    toggle(id) {
      const key = String(id);
      if (this.ids.has(key)) this.ids.delete(key);
      else this.ids.add(key);
      store.set(WISHLIST_KEY, [...this.ids]);
      this.render();
      document.dispatchEvent(new CustomEvent('jmt:wishlist:change', { detail: { ids: [...this.ids] } }));
    },
    render(root = document) {
      $$('[data-jmt-wishlist]', root).forEach((button) => {
        const saved = this.has(button.dataset.jmtWishlist);
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
      });
      const count = this.ids.size;
      $$('[data-jmt-wishlist-count]').forEach((dot) => {
        dot.hidden = count === 0;
        const link = dot.closest('a');
        if (link) link.setAttribute('aria-label', count ? `Wishlist, ${count} saved` : 'Wishlist');
      });
    },
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-jmt-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    wishlist.toggle(button.dataset.jmtWishlist);
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== WISHLIST_KEY) return;
    wishlist.ids = new Set(store.get(WISHLIST_KEY, []).map(String));
    wishlist.render();
  });
  wishlist.render();
})();
